import DashboardGenComponent from "@/components/DashboardGenComponent.tsx";
import React, {Fragment, useEffect, useRef, useState} from "react";
import CMS from "@/utils/cms";
import {LocalLanguage} from "@/types_and_interfaces/types";
import st from "@/styles/CreditLine.module.scss"
import {useTranslation} from "react-i18next";
import DashboardClass from "@/utils/dashboard_class";
import { useNavigate } from 'react-router-dom';
import LoanInterface from "@/types_and_interfaces/loan_interface.ts";
import useLoans from "@/hooks/loans.ts";
import {loansReqs} from "@/services/loans.ts";
import ModalLayout from "@/ui/modals/ModalLayout.tsx";
import {useAtom} from "jotai";
import {loan_sidebar_data, profile} from "@/atoms/profile.ts";
import eventEmitter from "@/utils/event_emmiter_instance.ts";
import CommonClass from "@/utils/common.ts";
import CreditLineRequestTransaction from "@/components/CreditLineRequestTransaction.tsx";
import clsx from "clsx";
import CreditLineDisbursedListItem from "@/components/CreditLineDisbursedListItem.tsx";
import CreditLineNotDisbursedListItem from "@/components/CreditLineNotDisbursedListItem.tsx";
import {ApplicationsAndCrLineStatuses} from "@/types_and_interfaces/enums.ts";
import CreditLineTransactionList from "@/components/CreditLineTransactionsList.tsx";

const CreditLineMain:React.FC = ({}) => {

    const {t, i18n} = useTranslation()
    const navigate = useNavigate()
    const cr_line_d = CMS["credit-line"]
    const data = cr_line_d ?  CMS.langAwareFilter(cr_line_d, (i18n.language as LocalLanguage)) : {}
    const dc = DashboardClass
    const [, set_data] = useAtom(loan_sidebar_data)
    const [user] = useAtom(profile)
    const loans = useLoans()
    const cr_line_loans = loans.getLoansByType("Credit Line")
    const cr_line_not_empty = !!(Array.isArray(cr_line_loans) && cr_line_loans.length)
    const cr_line_loans_sorted = cr_line_not_empty ? cr_line_loans?.sort((a,b)=> +b?.avail_capital - +a?.avail_capital) : []
    const cr_line_disbursed = cr_line_loans_sorted?.filter(loan => ["approved", "closed", "disbursed", "finished", "canceled"]?.includes(loan?.status))
    const cr_line_not_disbursed =  cr_line_loans_sorted?.filter(loan => !["approved", "closed", "disbursed", "finished", "canceled"]?.includes(loan?.status))
    const effect_ran = useRef<boolean>(false)
    const [request_transaction, set_request_transaction] = useState<LoanInterface | undefined>(undefined)
    const [money, setMoney] = useState<number>(5000)

    const get_cr_line = async () => {
        let req = await loansReqs().fetch_cr_line_loan_list()

        if(!req.error && req?.response.hasOwnProperty("data")) {
            if (!Array.isArray(req?.response.data) || (Array.isArray(req?.response.data) && !req?.response.data.length)) {
                navigate(dc.dashboard_routes.Home)
            }
        }
    }

    useEffect(() => {
        if(!effect_ran.current) {
            get_cr_line()
        }

        return () => {
            effect_ran.current = true
        }
    }, []);

    const apply = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        const form = new FormData(e.currentTarget)

        const amount = form.get("amount")

        if(!user?.phone.length) {
            eventEmitter.emit(CommonClass.ModalEvents.SetPhone, true, "", t("set_phone_to_continue"))
            return;
        }

        if(amount && Number(amount)) {
            if(Number(amount) < 3000) {
                eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "warning", t("min_mount_warn_cr_line"))
                return
            }
        }

        const data = {
            amount,
            // ...cr_line_data
        }

        set_data(JSON.stringify({
            amount: amount,
        }))

        if(Object.keys(data).length) {
            navigate(dc.dashboard_routes.ApplyCrLine + `/${request_transaction?.urn}`)
        }
    }

    return !!data && (
        <>
            <DashboardGenComponent
                header_type={"primary"}

                responsive1

                heading={
                    <div>
                        {t("line_credit1")}
                    </div>
                }

                column_center={
                    <div className={clsx(
                        st.column_center,
                    )}>

                        {(Array.isArray(cr_line_not_disbursed) && !!cr_line_not_disbursed.length) &&
                            Object.values(ApplicationsAndCrLineStatuses)
                                ?.sort((a, b) => a.localeCompare(b))
                                ?.map((key:ApplicationsAndCrLineStatuses, index) => {
                                    const current_cr_lines = cr_line_not_disbursed.filter((x) => x.status === key) || []
                                    return !!current_cr_lines.length && (
                                        <Fragment key={index}>
                                            <h3>
                                                {t("line_credit_title_"+key)}
                                            </h3>

                                            {current_cr_lines.map((obj: LoanInterface, i) => {
                                                return (
                                                    <CreditLineNotDisbursedListItem
                                                        key={`credit_line_not_disbursed_list_item_${i}`}
                                                        obj={obj}
                                                    />
                                                )
                                            })}
                                        </Fragment>
                                    )
                                })
                        }

                        {(Array.isArray(cr_line_disbursed) && !!cr_line_disbursed.length) &&
                            Object.values(ApplicationsAndCrLineStatuses).map((key:ApplicationsAndCrLineStatuses, index) => {
                                const cr_line_disbursed_sorted = [...cr_line_disbursed].sort((a, b) => {
                                    if (a.status === "canceled" && b.status !== "canceled") return 1;
                                    if (a.status !== "canceled" && b.status === "canceled") return -1;
                                    return 0;
                                });
                                const current_cr_lines = cr_line_disbursed_sorted.filter((x) => x.status === key) || []

                                return !!current_cr_lines.length && (
                                    <Fragment key={index}>
                                        <h3>
                                            {t("line_credit_title_"+key)}
                                        </h3>

                                        {current_cr_lines.map((obj: LoanInterface, i) => {
                                            return (
                                                <Fragment key={`credit_line_disbursed_list_item_${i}`}>
                                                    <CreditLineDisbursedListItem
                                                        obj={obj}
                                                        set_request_transaction={()=>set_request_transaction(obj)}
                                                    />

                                                    {["approved", "disbursed"]?.includes(obj?.status) && !!obj?.transactions?.length && (
                                                        <CreditLineTransactionList
                                                            is_cr_line_page
                                                            cr_line={obj as LoanInterface}
                                                        />
                                                    )}
                                                </Fragment>
                                            )
                                        })}
                                    </Fragment>
                                )
                            })
                        }
                    </div>
                }
            />

            <ModalLayout
                className={st.request_transaction_modal}
                open={Boolean(request_transaction)}
                innerClassName={st.request_transaction_modal_inner}
                on_close_state={() => {
                    set_request_transaction(undefined)
                }}
            >
                <CreditLineRequestTransaction
                    is_credit_line_page
                    money={money}
                    moneyHandler={(n: number) => {
                        setMoney(n)
                    }}
                    credit_line={request_transaction as LoanInterface}
                    onSubmit={apply}
                />
            </ModalLayout>
        </>
    )
}


export default CreditLineMain